import { useState } from "react";
import { useForm } from "react-hook-form";
import type { ImportRequestDetails } from "../../types";
import type { RequestDetailsCardProps } from "../PurchaseRequestsV2/NewRequest/RequestDetailsCard";
import { createFileHandlers } from "../PurchaseRequestsV2/NewRequest/requestFiles";
import { createGlobalHandlers } from "../PurchaseRequestsV2/NewRequest/requestGlobals";
import {
  emptyRequestFormValues,
  type RequestFormValues,
} from "../PurchaseRequestsV2/NewRequest/requestFormSchema";

const textOrNull = (value: string) => value.trim() || null;

/**
 * What the import's confirm carries from the details section. Blank text goes as null so the
 * backend keeps what the file says; a single date goes as a one-day range.
 */
export const toImportRequestDetails = (
  values: RequestFormValues,
  assignedBuyerId = ""
): ImportRequestDetails => {
  const from = values.globalNeededFrom || values.globalNeededTo || null;
  const to = values.singleDateMode ? from : values.globalNeededTo || from;
  const budget = values.globalBudgetValue;
  const hasBudget = budget !== null && !Number.isNaN(budget);

  return {
    name: textOrNull(values.name),
    purchaseRequestCode: textOrNull(values.purchaseRequestCode),
    comments: textOrNull(values.comments),
    neededFrom: from,
    neededTo: to,
    budgetCurrency: hasBudget ? values.globalCurrency : null,
    budgetValue: hasBudget ? budget : null,
    files: values.files.map((file) => ({
      fileURL: file.fileURL,
      name: file.name ?? "",
    })),
    assignedBuyerId: assignedBuyerId || null,
  };
};

/**
 * The create form's "Request details" section, for the spreadsheet import.
 *
 * Runs the same `RequestFormValues` form and the same date/budget/attachment handlers the create
 * page uses (`requestGlobals.ts`, `requestFiles.ts`), with no items — the file brings those — so
 * `RequestDetailsCard` renders here exactly as it does on the create page.
 */
export const useImportRequestDetails = () => {
  const { control, getValues, setValue, watch } = useForm<RequestFormValues>({
    defaultValues: emptyRequestFormValues,
  });
  const values = watch();
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  /** The buyer for the items the file assigns to nobody; chosen on the summary, not the card. */
  const [assignedBuyerId, setAssignedBuyerId] = useState("");

  const globals = createGlobalHandlers(getValues, setValue);
  const { handleAttachmentUpload, handleRemoveFile } = createFileHandlers(
    getValues,
    setValue,
    setIsUploadingAttachment
  );

  const cardProps: Omit<RequestDetailsCardProps, "disabled"> = {
    control,
    todayString: new Date().toISOString().split("T")[0],
    singleDateMode: values.singleDateMode,
    onSingleDateModeChange: globals.setSingleDateMode,
    globalNeededFrom: values.globalNeededFrom,
    onGlobalNeededFromChange: globals.handleGlobalNeededFromChange,
    globalNeededTo: values.globalNeededTo,
    onGlobalNeededToChange: globals.handleGlobalNeededToChange,
    globalCurrency: values.globalCurrency,
    onGlobalCurrencyChange: globals.handleGlobalCurrencyChange,
    globalBudgetValue: values.globalBudgetValue,
    onGlobalBudgetValueChange: globals.handleGlobalBudgetValueChange,
    files: values.files,
    isUploadingAttachment,
    onAttachmentUpload: handleAttachmentUpload,
    onRemoveFile: handleRemoveFile,
  };

  return {
    cardProps,
    isUploadingAttachment,
    assignedBuyerId,
    setAssignedBuyerId,
    toPayload: () => toImportRequestDetails(getValues(), assignedBuyerId),
  };
};
